<template>
  <AuthPageLayout>
    <div class="auth-card">
      <div class="auth-card__header">
        <div class="auth-card__logo">
          <BrandLogo size="md" />
        </div>
        <div class="auth-card__icon">
          <i class="bi bi-person-plus"></i>
        </div>
        <h1 class="auth-card__title">ایجاد حساب</h1>
        <p class="auth-card__subtitle">{{ stepSubtitle }}</p>
      </div>

      <ErrorMessage
        v-if="errorMessage"
        title="ادامه ممکن نشد"
        :message="errorMessage"
        :retryable="false"
        class="mb-3"
      />

      <form v-if="step === 1" @submit.prevent="onSendOtp" novalidate>
        <div class="mb-3">
          <label for="mobile" class="form-label">شماره موبایل</label>
          <div class="input-group">
            <span class="input-group-text"><i class="bi bi-telephone"></i></span>
            <input
              id="mobile"
              v-model.trim="mobile"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              class="form-control"
              :class="{ 'is-invalid': mobileError }"
              placeholder="مثال: 09123456789"
            />
          </div>
          <div v-if="mobileError" class="invalid-feedback d-block">{{ mobileError }}</div>
        </div>
        <button type="submit" class="btn auth-btn w-100" :disabled="loading">
          {{ loading ? "در حال ارسال کد..." : "ارسال کد تأیید" }}
        </button>
      </form>

      <form v-else-if="step === 2" @submit.prevent="onVerifyOtp" novalidate>
        <div class="mb-3">
          <label for="otp" class="form-label">کد تأیید</label>
          <input
            id="otp"
            v-model.trim="otpCode"
            type="text"
            inputmode="numeric"
            maxlength="6"
            class="form-control text-center otp-input"
            placeholder="کد ۶ رقمی"
          />
        </div>
        <button type="submit" class="btn auth-btn w-100 mb-2" :disabled="loading">
          {{ loading ? "در حال تأیید..." : "تأیید کد" }}
        </button>
        <button type="button" class="btn btn-link w-100" @click="step = 1">
          تغییر شماره موبایل
        </button>
      </form>

      <form v-else @submit.prevent="onRegister" novalidate>
        <div class="mb-3">
          <label for="fullName" class="form-label">نام کامل (اختیاری)</label>
          <input
            id="fullName"
            v-model.trim="fullName"
            type="text"
            autocomplete="name"
            class="form-control"
            placeholder="نام شما"
          />
        </div>
        <div class="mb-3">
          <label for="password" class="form-label">رمز عبور</label>
          <input
            id="password"
            v-model="password"
            type="password"
            autocomplete="new-password"
            class="form-control"
            :class="{ 'is-invalid': passwordError }"
            placeholder="حداقل ۸ کاراکتر"
          />
          <div v-if="passwordError" class="invalid-feedback d-block">{{ passwordError }}</div>
        </div>
        <div class="mb-3">
          <label for="confirmPassword" class="form-label">تکرار رمز عبور</label>
          <input
            id="confirmPassword"
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            class="form-control"
            :class="{ 'is-invalid': confirmError }"
          />
          <div v-if="confirmError" class="invalid-feedback d-block">{{ confirmError }}</div>
        </div>
        <button type="submit" class="btn auth-btn w-100" :disabled="loading">
          {{ loading ? "در حال ایجاد حساب..." : "ایجاد حساب" }}
        </button>
      </form>

      <p class="text-center mt-3 mb-0 auth-switch">
        قبلاً ثبت‌نام کرده‌اید؟
        <router-link to="/">ورود</router-link>
      </p>
    </div>
  </AuthPageLayout>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import AuthPageLayout from "@/components/auth/AuthPageLayout.vue";
import BrandLogo from "@/components/BrandLogo.vue";
import { followAuthRedirect } from "@/utils/authSession";

const router = useRouter();
const route = useRoute();
const { sendOtp, verifyOtp, register, loading, error } = useAuth();

const step = ref(1);
const mobile = ref("");
const otpCode = ref("");
const fullName = ref("");
const password = ref("");
const confirmPassword = ref("");
const verificationToken = ref("");
const mobileError = ref("");
const passwordError = ref("");
const confirmError = ref("");
const errorMessage = ref("");

const MOBILE_PATTERN = /^\+?\d{7,15}$/;

const stepSubtitle = computed(() => {
  if (step.value === 1) return "کد تأیید از طریق پیامک برای شما ارسال می‌شود";
  if (step.value === 2) return `کد ارسال‌شده به ${mobile.value} را وارد کنید`;
  return "رمز عبور برای ورودهای بعدی انتخاب کنید";
});

function normalizeMobile(value) {
  return value.replace(/[\s-]/g, "");
}

async function onSendOtp() {
  errorMessage.value = "";
  mobileError.value = "";
  const normalized = normalizeMobile(mobile.value);
  if (!normalized || !MOBILE_PATTERN.test(normalized)) {
    mobileError.value = "شماره موبایل معتبر وارد کنید.";
    return;
  }
  mobile.value = normalized;
  const ok = await sendOtp(normalized, "signup");
  if (ok) step.value = 2;
  else errorMessage.value = error.value || "ارسال کد تأیید انجام نشد.";
}

async function onVerifyOtp() {
  errorMessage.value = "";
  if (!otpCode.value || otpCode.value.length < 4) {
    errorMessage.value = "کد تأیید پیامک را وارد کنید.";
    return;
  }
  const token = await verifyOtp(mobile.value, otpCode.value, "signup");
  if (!token) {
    errorMessage.value = error.value || "کد تأیید نامعتبر است.";
    return;
  }
  verificationToken.value = token;
  step.value = 3;
}

async function onRegister() {
  errorMessage.value = "";
  passwordError.value = "";
  confirmError.value = "";
  if (password.value.length < 8) {
    passwordError.value = "رمز عبور باید حداقل ۸ کاراکتر باشد.";
    return;
  }
  if (password.value !== confirmPassword.value) {
    confirmError.value = "رمز عبور و تکرار آن یکسان نیست.";
    return;
  }
  const ok = await register({
    phone_number: mobile.value,
    password: password.value,
    full_name: fullName.value || null,
    verification_token: verificationToken.value,
  });
  if (ok) {
    const redirect =
      typeof route.query.redirect === "string" ? route.query.redirect : "/dashboard";
    followAuthRedirect(redirect, "/dashboard", (path) => {
      router.push(path);
    });
  } else {
    errorMessage.value = error.value || "ایجاد حساب انجام نشد.";
  }
}
</script>
